"use client";

import { useStylesConfigValue } from "@health/ui";
import { Avatar } from "@health/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@health/ui/components/dropdown-menu";
import { CircleUserIcon } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { dispatchAuthChange, onAuthChange } from "@/lib/auth/auth-events";
import AiTextIcon from "../../assets/ai-text-icon.svg?react";

// Lazy-load the AuthWidget chunk — it only mounts when the user clicks the
// login button, so there's no reason to ship it in the main bundle. Matches
// the lazy registry in @health/sdk-react so both entry points split the
// same chunk.
const AuthWidget = lazy(() =>
  import("@health/sdk-react/widgets").then((m) => ({ default: m.AuthWidget })),
);

type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

/**
 * Authentication header component
 * Shows login button when unauthenticated, avatar menu when authenticated
 */
export function AuthHeader({ baseUrl }: { baseUrl?: string }) {
  const [user, setUser] = useState<User | null>(null);
  const [showAuthWidget, setShowAuthWidget] = useState(false);
  const [loading, setLoading] = useState(true);
  const profileJourneyIcon = useStylesConfigValue("profileJourneyIcon");
  const resolvedBaseUrl = baseUrl ?? import.meta.env.VITE_COMPANION_SERVICE_URL ?? "";

  useEffect(() => {
    checkAuth();

    // Listen for auth changes from other components
    const cleanup = onAuthChange(() => {
      checkAuth();
    });

    return cleanup;
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${trimTrailingSlash(resolvedBaseUrl)}/v1/auth/me`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.authenticated) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${trimTrailingSlash(resolvedBaseUrl)}/v1/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      setUser(null);
      dispatchAuthChange();
      // Reload to clear any cached state
      window.location.reload();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleAuthSubmit = (result: unknown) => {
    const authResult = result as Record<string, unknown>;
    if (
      authResult.action === "signup" ||
      authResult.action === "signin" ||
      authResult.action === "email_login"
    ) {
      // Refresh auth state after successful auth
      checkAuth();
      dispatchAuthChange();
    }
    setShowAuthWidget(false);
  };

  if (loading) {
    return <div className="w-24" />;
  }

  const getAuthButton = () => {
    if (user) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="User menu"
            >
              <Avatar
                firstName={user.firstName || user.email}
                lastName={user.lastName}
                className="w-8 h-8 text-xs"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-semibold">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-gray-500 text-ellipsis overflow-hidden">{user.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <>
        <button
          onClick={() => setShowAuthWidget(true)}
          className="font-semibold text-black hover:scale-105 transition-transform cursor-pointer py-3"
        >
          <CircleUserIcon className="size-7" />
        </button>

        {showAuthWidget && (
          <Suspense fallback={null}>
            <AuthWidget
              input={{ screen: "signin" }}
              output={null}
              onSubmit={handleAuthSubmit}
              submitted={false}
              modalOnly
            />
          </Suspense>
        )}
      </>
    );
  };

  return (
    <div className="flex items-center gap-4 md:gap-6">
      {profileJourneyIcon === "show" && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("journey:open-panel"))}
          aria-label="View your journey"
          className="font-semibold text-black hover:scale-105 transition-transform cursor-pointer py-3"
        >
          <AiTextIcon className="size-7" />
        </button>
      )}

      {getAuthButton()}
    </div>
  );
}
